import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical, PencilLine, Plus, Trash2 } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { renderAsteriskBold } from '../utils/emphasis';
import { htmlToPlainText, sanitizeHtml } from '../utils/html';

export type RelevantProjectExperienceItem = {
  id: string;
  title: string;
  content: string;

  sourceId?: string;
  evidenceType?: string;
  relevanceScore?: number;
};

export interface RelevantProjectExperienceProps {
  items?: RelevantProjectExperienceItem[];

  draftItems?: RelevantProjectExperienceItem[];
  onDraftItemsChange?: (items: RelevantProjectExperienceItem[]) => void;
  onCreateDraftVersion?: (items: RelevantProjectExperienceItem[]) => void;
  onCancelDraft?: () => void;
  saveCommitId?: number;
}

function SortableItemWrapper({
  id,
  children,
}: {
  id: string;
  children: (handleProps: React.HTMLAttributes<HTMLElement>) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : undefined,
    position: 'relative',
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
}

export default function RelevantProjectExperience({
  items,
  draftItems,
  onDraftItemsChange,
  onCreateDraftVersion,
  onCancelDraft,
  saveCommitId,
}: RelevantProjectExperienceProps) {
  const COLLAPSE_KEY = 'one_pager_section_expanded__relevant_project_experience';
  const rootRef = useRef<HTMLDivElement | null>(null);
  const isProbablyHtml = (value: string) => /<\s*[a-z][\s\S]*>/i.test(value);

  const renderContent = (value: string) => {
    if (isProbablyHtml(value)) {
      return <span className="[&_em]:italic [&_i]:italic" dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }} />;
    }
    return renderAsteriskBold(value);
  };

  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(COLLAPSE_KEY) !== 'false';
    } catch {
      return true;
    }
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editItems, setEditItems] = useState<RelevantProjectExperienceItem[]>([]);
  const [snapshotItems, setSnapshotItems] = useState<RelevantProjectExperienceItem[]>([]);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addContent, setAddContent] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const effectiveItems = useMemo(() => {
    const base = items ?? [];
    return draftItems ?? base;
  }, [items, draftItems]);

  useEffect(() => {
    setIsEditing(false);
    setEditItems([]);
    setSnapshotItems([]);
    setConfirmDiscardOpen(false);
    setAddModalOpen(false);
    setAddTitle('');
    setAddContent('');
  }, [saveCommitId]);

  if (effectiveItems.length === 0) return null;

  const hasChanges = JSON.stringify(editItems) !== JSON.stringify(snapshotItems);

  const toggleExpanded = () => {
    if (isEditing) return;
    setIsExpanded((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const startEditing = () => {
    const snapshot = effectiveItems.map((i) => ({ ...i }));
    setSnapshotItems(snapshot);
    setEditItems(snapshot.map((i) => ({ ...i })));
    setIsEditing(true);
    setIsExpanded(true);
    try {
      sessionStorage.setItem(COLLAPSE_KEY, 'true');
    } catch {
      // ignore
    }
  };

  const handleSave = () => {
    onDraftItemsChange?.(editItems);
    onCreateDraftVersion?.(editItems);
    setIsEditing(false);
  };

  const handleCancelRequest = () => {
    if (hasChanges) {
      setConfirmDiscardOpen(true);
    } else {
      setIsEditing(false);
    }
  };

  const confirmDiscard = () => {
    onCancelDraft?.();
    setIsEditing(false);
    setConfirmDiscardOpen(false);
    setEditItems([]);
    setSnapshotItems([]);
  };

  const updateEditItem = (id: string, patch: Partial<RelevantProjectExperienceItem>) => {
    setEditItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const handleDelete = (id: string) => {
    setEditItems((prev) => prev.filter((it) => it.id !== id));
  };

  const createClientId = () => {
    try {
      return `temp-${crypto.randomUUID()}`;
    } catch {
      return `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  };

  const addExperience = () => {
    const nextTitle = addTitle.trim();
    const nextContent = addContent;
    if (!nextTitle || !htmlToPlainText(nextContent)) return;
    setEditItems((prev) => [
      ...prev,
      { id: createClientId(), title: nextTitle, content: nextContent },
    ]);
    setAddModalOpen(false);
    setAddTitle('');
    setAddContent('');
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setEditItems((prev) => {
      const oldIndex = prev.findIndex((i) => i.id === active.id);
      const newIndex = prev.findIndex((i) => i.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const displayItems = isEditing ? editItems : effectiveItems;

  return (
    <div ref={rootRef} className="w-full">
      {/* Header */}
      <div className="flex items-start justify-between py-4">
        <button type="button" className="flex items-start gap-[13px] text-left" onClick={toggleExpanded}>
          <div className="flex size-[24px] items-center justify-center">
            <div className="flex-none">
              {isExpanded ? <ChevronDown size={24} color="#25ADE3" /> : <ChevronRight size={24} color="#25ADE3" />}
            </div>
          </div>
          <p
            className="flex-1 font-['Hanken_Grotesk',sans-serif] text-[24px] font-bold leading-[36px] tracking-[0.75px] text-[#132543]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            Relevant Project Experience
          </p>
        </button>

        {!isEditing && (
          <button
            type="button"
            className="inline-flex items-center gap-[6px] text-center font-['Inter',sans-serif] text-[14px] font-medium leading-[20px] tracking-[-0.1504px] text-[#53BEE8]"
            onClick={startEditing}
            data-pdf-exclude
          >
            <PencilLine size={16} color="#53BEE8" />
            Edit
          </button>
        )}

        {isEditing && (
          <div className="flex items-center gap-2" data-pdf-exclude>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md border border-[rgba(0,0,0,0.1)] bg-white px-3 py-1.5 text-sm font-medium"
              onClick={() => {
                setAddModalOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Add Experience
            </button>
            <button
              type="button"
              className="px-3 py-1 rounded bg-[#29446D] text-white text-sm font-medium"
              onClick={handleSave}
            >
              Save
            </button>
            <button
              type="button"
              className="px-3 py-1 rounded border border-gray-200 text-[#29446D] text-sm font-medium"
              onClick={handleCancelRequest}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Items */}
      <div className={'pb-6 ' + (isExpanded ? 'block' : 'hidden')}>
        {isEditing ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={editItems.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-8">
                {editItems.map((item) => (
                  <SortableItemWrapper key={item.id} id={item.id}>
                    {(handleProps) => (
                      <div className="rounded-[12px] border border-[rgba(0,0,0,0.1)] bg-white p-4">
                        <div className="flex items-start gap-2">
                          <button
                            type="button"
                            className="mt-1 shrink-0 h-8 w-6 p-0 inline-flex items-center justify-center rounded cursor-grab active:cursor-grabbing touch-none"
                            aria-label="Drag to reorder"
                            {...handleProps}
                          >
                            <GripVertical className="h-4 w-4 text-gray-400" />
                          </button>

                          <div className="flex-1 min-w-0 space-y-3">
                            <input
                              value={item.title}
                              onChange={(e) => updateEditItem(item.id, { title: e.target.value })}
                              className="w-full rounded border border-[rgba(0,0,0,0.15)] bg-white px-3 py-2 text-sm font-semibold"
                              placeholder="Title"
                            />
                            <div className="snapshot-quill bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                              <ReactQuill
                                theme="snow"
                                value={item.content}
                                onChange={(value) => updateEditItem(item.id, { content: value })}
                                modules={{
                                  toolbar: [
                                    [{ header: [1, 2, false] }],
                                    ['bold', 'italic', 'underline'],
                                    [{ list: 'ordered' }, { list: 'bullet' }],
                                    ['link'],
                                    ['clean'],
                                  ],
                                }}
                              />
                            </div>
                            <button
                              type="button"
                              className="inline-flex items-center gap-2 rounded px-2 py-1 text-sm font-medium text-black hover:bg-red-50"
                              onClick={() => handleDelete(item.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </SortableItemWrapper>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="space-y-8">
            {displayItems.map((item) => (
              <div key={item.id} className="rounded-[12px] border border-[rgba(0,0,0,0.1)] bg-white p-4">
                <div className="mb-3">
                  <h3 className="text-sm font-semibold">{renderContent(item.title)}</h3>
                </div>
                <div className="text-sm text-gray-700 leading-relaxed">
                  {isProbablyHtml(item.content) ? (
                    <div
                      className="ql-snow"
                      dangerouslySetInnerHTML={{ __html: `<div class="ql-editor">${sanitizeHtml(item.content)}</div>` }}
                    />
                  ) : (
                    <p>{renderContent(item.content)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Experience Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 z-[70]">
          <div className="absolute inset-0 bg-black/50" onClick={() => setAddModalOpen(false)} />
          <div className="relative mx-auto mt-24 w-full max-w-[720px] rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-bold text-gray-900">Add New Experience</h3>
              <button
                type="button"
                className="rounded px-2 py-1 text-sm text-gray-600 hover:bg-gray-50"
                onClick={() => setAddModalOpen(false)}
              >
                Close
              </button>
            </div>

            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  className="w-full rounded border border-[rgba(0,0,0,0.1)] bg-white px-3 py-2 text-sm font-semibold"
                  value={addTitle}
                  onChange={(e) => setAddTitle(e.target.value)}
                  placeholder="Title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <div className="snapshot-quill bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={addContent}
                    onChange={(value) => setAddContent(value)}
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['link'],
                        ['clean'],
                      ],
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-2">
              <div className="flex-1" />
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md border border-[rgba(0,0,0,0.1)] bg-white px-3 py-1.5 text-sm font-medium"
                onClick={() => setAddModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md bg-[#29446D] px-3 py-1.5 text-sm font-medium text-white"
                onClick={addExperience}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved changes confirm modal */}
      {confirmDiscardOpen && (
        <div className="fixed inset-0 z-[80]">
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative mx-auto mt-40 w-full max-w-[440px] rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-base font-bold text-gray-900">Discard unsaved changes?</h3>
            <p className="mt-2 text-sm text-gray-600">
              You have unsaved changes that will be discarded if you cancel now.
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded border border-gray-200 text-sm font-medium text-[#29446D]"
                onClick={() => setConfirmDiscardOpen(false)}
              >
                Go back
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded bg-red-600 text-white text-sm font-medium"
                onClick={confirmDiscard}
              >
                Discard changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
